import { Buyer } from '@40seas/entities/buyers';
import { ChargeType } from '@40seas/entities/charges';
import { Checkout, CheckoutStatus } from '@40seas/entities/checkout';
import {
    Country,
    CreditLineProvider,
    CreditLineResultStatus,
    EntityReferenceType,
    Totals,
    EntityReferenceWithAmount,
} from '@40seas/entities/common';
import { CreditLine } from '@40seas/entities/credit-line';
import { CurrencyExchange, CurrencyExchangeProviderType, Quote } from '@40seas/entities/currency-exchange';
import { FeeDefinition, FeeDefinitionStatus, Fee as FeeEntity } from '@40seas/entities/fees';
import { Invoice } from '@40seas/entities/invoices';
import { ValidationError } from '@40seas/errors';
import { formatAmount } from '@40seas/formatters';
import {
    BusinessType,
    Currency,
    Discount,
    Fee,
    PaymentMethodType,
    PaymentOption,
    PaymentOptionWithFee,
    PaymentTerm,
    PaymentTermTiming,
    PaymentTermType,
    PaymentTermsOptions,
} from '@40seas/models';
import { BankConnectivityProvidersService } from '@40seas/shared/bank-connectivity-providers-service';
import { getRoundedDecimal } from '@40seas/shared/calculations';
import { FeeDefinitionType } from 'libs/entities/fees/src/lib/fee-definition.type.enum';
import { FeeEntityType } from 'libs/entities/fees/src/lib/fee.entity-type.enum';
import capitalise from 'lodash/capitalize';
import moment, { Moment } from 'moment';
import { CHECKOUT_PENDING_STATUSES } from './consts';

export const calculateAmount = (
    chargeAmount: number,
    feeDefinitions: FeeDefinition[],
    checkoutAmount: number,
    checkoutTotalAmount: number,
    currencyExchange?: CurrencyExchange,
    calculateTotalsCXFt?: boolean,
): number => {
    if (chargeAmount === checkoutTotalAmount) {
        return checkoutAmount ?? 0;
    }

    let amount = chargeAmount;

    const financingFee = feeDefinitions.find((feeDefinition) => feeDefinition.type === FeeDefinitionType.Financing)?.buyerFee;
    const transactionFee = feeDefinitions.find((feeDefinition) => feeDefinition.type === FeeDefinitionType.Transaction)?.buyerFee;

    if (transactionFee) {
        const fixedTransactionFee = !calculateTotalsCXFt
            ? (transactionFee.fixed ?? 0)
            : getRoundedDecimal((transactionFee.fixed ?? 0) * (currencyExchange?.rate ?? 1));
        amount = getRoundedDecimal((amount - fixedTransactionFee) / (1 + (transactionFee.percentage ?? 0) / 100));
    }

    if (financingFee) {
        const fixedFinancingFee = !calculateTotalsCXFt
            ? (financingFee.fixed ?? 0)
            : getRoundedDecimal((financingFee.fixed ?? 0) * (currencyExchange?.rate ?? 1));
        amount = getRoundedDecimal((amount - fixedFinancingFee) / (1 + (financingFee.percentage ?? 0) / 100));
    }

    return getOriginalCurrencyAmount(amount, currencyExchange);
};

export const calculateTotals = (
    amount: number | undefined,
    feeDefinitions: FeeDefinition[],
    currencyExchange?: CurrencyExchange,
    calculateTotalsCXFt?: boolean,
): Totals => {
    if (amount === undefined) return {} as Totals;

    const financingFee = feeDefinitions.find((feeDefinition) => feeDefinition.type === FeeDefinitionType.Financing);
    const transactionFee = feeDefinitions.find((feeDefinition) => feeDefinition.type === FeeDefinitionType.Transaction);

    const { finalAmount: amountWithCurrencyExchange, currencyExchangeFeeAmount } = getNewCurrencyAmount(amount, currencyExchange);

    const {
        buyerFeeAmount: financingFeeAmount,
        supplierFeeAmount: supplierFinancingFeeAmount,
        charge: financingCharge,
        payout: financingPayout,
    } = !calculateTotalsCXFt
        ? calculateFees(amountWithCurrencyExchange, amountWithCurrencyExchange, financingFee)
        : calculateFeesWithCx(amountWithCurrencyExchange, amount, financingFee, currencyExchange);

    const {
        buyerFeeAmount: transactionFeeAmount,
        supplierFeeAmount: supplierTransactionFeeAmount,
        charge: transactionCharge,
        payout: transactionPayout,
    } = !calculateTotalsCXFt
        ? calculateFees(financingCharge, financingPayout, transactionFee)
        : calculateFeesWithCx(financingCharge, financingPayout, transactionFee, currencyExchange);

    /* We leave out the currencyExchangeFeeAmount from the buyerFeeAmount because we would like 
     to show the buyer the total amount with the currency exchange fee in the new currency */
    const buyerFeeAmount = transactionFeeAmount + financingFeeAmount;

    const supplierFeeAmount = supplierTransactionFeeAmount + supplierFinancingFeeAmount;

    const fees: Pick<FeeEntity, 'feeDefinition' | 'amount' | 'entityType' | 'businessType'>[] = [];

    if (transactionFee) {
        fees.push({
            feeDefinition: transactionFee.id,
            amount: transactionFeeAmount,
            entityType: FeeEntityType.Charge,
            businessType: BusinessType.Buyer,
        });
        fees.push({
            feeDefinition: transactionFee.id,
            amount: supplierTransactionFeeAmount,
            entityType: FeeEntityType.Payout,
            businessType: BusinessType.Seller,
        });
    }

    if (financingFee) {
        fees.push({
            feeDefinition: financingFee.id,
            amount: financingFeeAmount,
            entityType: FeeEntityType.Invoice,
            businessType: BusinessType.Buyer,
        });
        fees.push({
            feeDefinition: financingFee.id,
            amount: supplierFinancingFeeAmount,
            entityType: FeeEntityType.Invoice,
            businessType: BusinessType.Seller,
        });
    }

    if (currencyExchange) {
        fees.push({
            feeDefinition: currencyExchange.feeDefinition,
            amount: currencyExchangeFeeAmount,
            entityType: FeeEntityType.Charge,
            businessType: BusinessType.Buyer,
        });
    }

    return {
        buyerFeeAmount,
        supplierFeeAmount,
        charge: transactionCharge,
        payout: transactionPayout,
        currencyExchangeFeeAmount,
        transactionFeeAmount,
        financingFeeAmount,
        fees,
    };
};

export const calculateTotalsBuyer = (
    amount: number | undefined,
    paymentFees: Fee[],
    currencyExchange?: CurrencyExchange,
    calculateTotalsCXFt?: boolean,
): Totals => {
    const financingFee = paymentFees.find((paymentFee) => paymentFee.feeDefinitionType === FeeDefinitionType.Financing);
    const transactionFee = paymentFees.find((paymentFee) => paymentFee.feeDefinitionType === FeeDefinitionType.Transaction);

    let charge = amount ?? 0;

    const financingFeeAmount = financingFee
        ? calculateFeeAmount(charge, financingFee, calculateTotalsCXFt ? currencyExchange : undefined)
        : 0;

    charge += financingFeeAmount;

    const transactionFeeAmount = transactionFee
        ? calculateFeeAmount(charge, transactionFee, calculateTotalsCXFt ? currencyExchange : undefined)
        : 0;

    charge += transactionFeeAmount;

    const buyerFeeAmount = transactionFeeAmount + financingFeeAmount;

    return {
        buyerFeeAmount,
        charge,
        transactionFeeAmount,
        financingFeeAmount,
    };
};

export const calculateFees = (
    chargeAmount: number,
    payoutAmount: number,
    feeDefinition?: FeeDefinition,
): { buyerFeeAmount: number; supplierFeeAmount: number; charge: number; payout: number } => {
    if (!feeDefinition) {
        return { buyerFeeAmount: 0, supplierFeeAmount: 0, charge: chargeAmount, payout: payoutAmount };
    }

    const { buyerFee, supplierFee } = feeDefinition;

    const buyerFeeAmount = calculateFeeAmount(chargeAmount, buyerFee);

    const charge = getRoundedDecimal(chargeAmount + buyerFeeAmount);

    const supplierFeeAmount = calculateFeeAmount(payoutAmount, supplierFee);

    const payout = getRoundedDecimal(payoutAmount - supplierFeeAmount);

    return {
        buyerFeeAmount,
        supplierFeeAmount,
        charge,
        payout,
    };
};

export const calculateFeesWithCx = (
    chargeAmount: number,
    payoutAmount: number,
    feeDefinition?: FeeDefinition,
    currencyExchange?: CurrencyExchange,
): { buyerFeeAmount: number; supplierFeeAmount: number; charge: number; payout: number } => {
    if (!feeDefinition) {
        return { buyerFeeAmount: 0, supplierFeeAmount: 0, charge: chargeAmount, payout: payoutAmount };
    }

    const { buyerFee, supplierFee } = feeDefinition;

    const buyerFeeAmount = calculateFeeAmount(chargeAmount, buyerFee, currencyExchange);

    const charge = chargeAmount + buyerFeeAmount;

    const supplierFeeAmount = calculateFeeAmount(payoutAmount, supplierFee);

    const payout = payoutAmount - supplierFeeAmount;

    return {
        buyerFeeAmount,
        supplierFeeAmount,
        charge,
        payout,
    };
};

export const calculateFeeAmount = (amount: number, fee: Fee, currencyExchange?: CurrencyExchange): number => {
    if (!currencyExchange) {
        return getRoundedDecimal((amount * (fee.percentage ?? 0)) / 100 + (fee.fixed ?? 0));
    }

    return getRoundedDecimal((amount * (fee.percentage ?? 0)) / 100 + getRoundedDecimal((fee.fixed ?? 0) * currencyExchange.rate));
};

const getNewCurrencyAmount = (
    amount: number,
    currencyExchange?: CurrencyExchange,
): { finalAmount: number; currencyExchangeFeeAmount: number } => {
    if (!currencyExchange) {
        return { finalAmount: amount, currencyExchangeFeeAmount: 0 };
    }
    const finalAmount = getRoundedDecimal(amount * currencyExchange.rate);

    const amountWithoutFee = getRoundedDecimal(amount * currencyExchange.marketRate);

    return { finalAmount, currencyExchangeFeeAmount: getRoundedDecimal(finalAmount - amountWithoutFee) };
};

export const getOriginalCurrencyAmount = (amount: number, currencyExchange?: CurrencyExchange): number => {
    if (!currencyExchange) {
        return amount;
    }

    return getRoundedDecimal(amount / currencyExchange.rate);
};

export const convertFeeDefinitionToPaymentOptionWithFee = (feeDefinitions: FeeDefinition[]): PaymentOptionWithFee[] => {
    if (!feeDefinitions) return [];

    return feeDefinitions
        .filter(
            (feeDefinition) =>
                !(feeDefinition.type === FeeDefinitionType.Transaction && feeDefinition.paymentTerm?.type === PaymentTermType.Net),
        )
        .flatMap((feeDefinition) => {
            const feePaymentMethods = feeDefinition.paymentMethods || [];
            return feePaymentMethods.map((paymentMethod) => {
                return {
                    paymentOption: {
                        paymentMethod: paymentMethod,
                        paymentTerm: feeDefinition.paymentTerm || { type: PaymentTermType.Immediate },
                    },
                    paymentFee: feeDefinition.buyerFee, // TODO: FS-5663 remove after removing paymentFee field
                    feeDefinition: feeDefinition.id, // TODO: FS-5663 remove after removing paymentFee field
                    paymentFees: [feeDefinition.buyerFee],
                };
            });
        });
};

export const addTransactionFees = (
    paymentOptionsWithFees: PaymentOptionWithFee[],
    feeDefinitions: FeeDefinition[],
): PaymentOptionWithFee[] => {
    return paymentOptionsWithFees
        .map((paymentOptionWithFee) => {
            // TODO: FS-5663 remove ? after removing optional
            if (!paymentOptionWithFee.paymentFees?.length || paymentOptionWithFee.paymentFees?.length > 1) {
                return paymentOptionWithFee;
            }

            // TODO: FS-5663 remove ? after removing optional
            if (paymentOptionWithFee.paymentFees[0].feeDefinitionType === FeeDefinitionType.Transaction) {
                return paymentOptionWithFee;
            }

            const transactionFeeDefinition = findTransactionPaymentFees(paymentOptionWithFee.paymentOption.paymentMethod, feeDefinitions);

            if (!transactionFeeDefinition) {
                return undefined;
            }

            return {
                ...paymentOptionWithFee,
                paymentFees: [...paymentOptionWithFee.paymentFees, transactionFeeDefinition.buyerFee],
            };
        })
        .filter((paymentOptionWithFee) => !!paymentOptionWithFee) as PaymentOptionWithFee[];
};

const findTransactionPaymentFees = (paymentMethodType: PaymentMethodType, feeDefinitions: FeeDefinition[]): FeeDefinition | undefined => {
    const allTransactionFeeDefinitions = feeDefinitions.filter(
        (feeDefinition) =>
            // TODO: FS-5663  after removing optional change to net
            feeDefinition.paymentTerm?.type !== PaymentTermType.Immediate &&
            feeDefinition.status === FeeDefinitionStatus.Active &&
            (feeDefinition.paymentMethods.includes(paymentMethodType) || !feeDefinition.paymentMethods.length) &&
            feeDefinition.type === FeeDefinitionType.Transaction,
    );

    const netTransactionFeeDefinition = allTransactionFeeDefinitions.find(
        (feeDefinition) => feeDefinition.paymentTerm?.type === PaymentTermType.Net,
    );

    if (netTransactionFeeDefinition) {
        return netTransactionFeeDefinition;
    }

    return allTransactionFeeDefinitions[0];
};

export const buildCustomPaymentOptions = (
    buyerPaymentOptions: PaymentOptionWithFee[],
    checkoutPaymentOptions?: PaymentOptionWithFee[],
    checkoutPaymentOptionsByPaymentTerms?: PaymentOptionWithFee[],
): PaymentOptionWithFee[] => {
    if (checkoutPaymentOptionsByPaymentTerms?.length) {
        return checkoutPaymentOptionsByPaymentTerms;
    }

    return checkoutPaymentOptions?.length ? checkoutPaymentOptions : buyerPaymentOptions?.length ? buyerPaymentOptions : [];
};

export const prettyPaymentTerm = (paymentTerm?: PaymentTerm): string | undefined => {
    if (!paymentTerm) return undefined;

    if (paymentTerm.type === PaymentTermType.Immediate) {
        return capitalise(PaymentTermType.Immediate);
    }

    if (paymentTerm.type === PaymentTermType.Net) {
        return paymentTerm.payload?.days ? `Net ${paymentTerm.payload?.days}` : capitalise(PaymentTermType.Net);
    }

    return undefined;
};

const countFromTextMap = {
    [PaymentTermTiming.FromShipping]: 'shipping',
    [PaymentTermTiming.FromInvoice]: 'invoice',
    [PaymentTermTiming.FromArrival]: 'arrival',
    [PaymentTermTiming.FromDueDate]: 'dueDate',
};

export const paymentTermTimingToDate = new Map<PaymentTermTiming, string>([
    [PaymentTermTiming.FromArrival, 'arrivalDate'],
    [PaymentTermTiming.FromInvoice, 'issueDate'],
    [PaymentTermTiming.FromShipping, 'shippingDate'],
    [PaymentTermTiming.FromDueDate, 'dueDate'],
]);

export const calculateCountFromText = (countFrom?: PaymentTermTiming) =>
    countFromTextMap[!countFrom ? PaymentTermTiming.FromInvoice : countFrom];

export const calculatePayNowFee = (amount: number, fee?: Fee): number => {
    let calculatedFee = 0;
    if (fee?.percentage) {
        calculatedFee += (amount * fee.percentage) / 100;
    }

    if (fee?.fixed) {
        calculatedFee += fee.fixed;
    }

    return calculatedFee;
};

export const getFeeCurrencyByPaymentMethod = (paymentMethods?: PaymentMethodType[]) => {
    if (!paymentMethods?.length || paymentMethods?.length > 1) {
        return Currency.USD;
    }

    return BankConnectivityProvidersService.getCurrencyByPaymentMethod(paymentMethods?.[0])?.[0];
};

export const parsePaymentFee = (
    fee: Fee | undefined,
    options?: {
        currency?: string;
        noValueTemplate?: string;
        currencyExchange?: CurrencyExchange;
    },
): string => {
    if (!fee) {
        return options?.noValueTemplate ?? '';
    }

    const percentage = fee.percentage && `${fee.percentage}%`;
    const fixed = !options?.currencyExchange
        ? fee.fixed &&
          formatAmount({
              value: fee.fixed,
              currency: options?.currency,
          })
        : formatAmount({
              value: getRoundedDecimal((fee.fixed ?? 0) * options?.currencyExchange?.rate),
              currency: options?.currency,
          });

    if (!percentage && !fixed) {
        return options?.noValueTemplate !== undefined ? options?.noValueTemplate : 'Free';
    }

    if (!fixed && percentage) {
        return `${fee?.percentage !== undefined && fee.percentage > 0 ? '+' : ''}${percentage} fee`;
    }

    if (fixed && percentage) {
        return `${fee?.percentage !== undefined && fee.percentage > 0 ? '+' : ''}${percentage} + ${fixed} fee`;
    }

    if (fixed && !percentage) {
        return `${fee?.fixed !== undefined && fee.fixed > 0 ? '+' : ''}${fixed} fee`;
    }

    return [percentage, fixed].filter(Boolean).join(' + ');
};

export const parsePayNowFixPaymentFee = (
    amount: number,
    fee: Fee | undefined,
    discounts: Discount[] | undefined,
    options?: {
        currency?: string;
        noValueTemplate?: string;
    },
): string | undefined => {
    if (!fee) {
        return options?.noValueTemplate ?? '';
    }

    const payNowFee = calculatePayNowFee(amount, fee);

    if (!payNowFee) {
        return options?.noValueTemplate !== undefined ? options?.noValueTemplate : 'Free';
    }

    const discountAmount = amount - calculateAmountAfterDiscount(amount, discounts ?? []);

    const finalFee = payNowFee - discountAmount;

    if (finalFee === 0) {
        return '';
    }

    const totalFee = formatAmount({
        value: finalFee,
        currency: options?.currency,
    });
    return totalFee;
};

export const calculateBuyerPaymentFee = (paymentFee: Fee | undefined, adjustment: Fee | undefined) => {
    const calculatedFee = (base: number | undefined, adjustment: number | undefined) => getRoundedDecimal((base || 0) + (adjustment || 0));

    const fee = {
        fixed: calculatedFee(paymentFee?.fixed, adjustment?.fixed),
        percentage: calculatedFee(paymentFee?.percentage, adjustment?.percentage),
    };

    return parsePaymentFee(fee, { noValueTemplate: '0%' });
};

export const isInvoiceTransitEnabled = (tenantId?: string): boolean => {
    return !!tenantId && /^ZIM(-.*)?$/.test(tenantId);
};

export const constructCashBalanceProviderId = (buyerId: string, currency: string): string => {
    return `${buyerId}-${currency.toUpperCase()}`;
};

export const calculateAmountAfterDiscount = (amount: number | undefined, discounts: Discount[] = []): number => {
    return discounts.reduce((acc, discount) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore - Fix TS FS-3199
        const percentageDiscount = (discount.percentage / 100) * acc;
        return getRoundedDecimal(acc - percentageDiscount - (discount.fixed || 0));
    }, amount || 0);
};

export const isBankPaymentMethod = (type: PaymentMethodType): boolean => {
    return [PaymentMethodType.ACH, PaymentMethodType.ACSS, PaymentMethodType.BACS, PaymentMethodType.BECS, PaymentMethodType.SEPA].includes(
        type,
    );
};

export const isLogCharge = (type: ChargeType): boolean => {
    return [ChargeType.Log, ChargeType.MarkedAsPaid].includes(type);
};

export const allSamePaymentOptions = (paymentOptions: PaymentOptionWithFee[] | undefined): boolean => {
    if (!paymentOptions) {
        return false;
    }

    return paymentOptions.every((option, index, array) => index === 0 || option.feeDefinition === array[0].feeDefinition);
};

export const getPaymentOptionKey = (paymentOption: PaymentOption): string => {
    const daysPart =
        paymentOption.paymentTerm.type === PaymentTermType.Immediate
            ? ''
            : paymentOption.paymentTerm.payload?.days
              ? `${paymentOption.paymentTerm.payload.countFrom}-${paymentOption.paymentTerm.payload.days}`
              : '';
    return `${paymentOption.paymentMethod}-${paymentOption.paymentTerm.type}-${daysPart}`;
};

export const canConfirmCheckout = (checkout: Checkout): boolean => {
    if (getIsCheckoutPending(checkout.status)) {
        return true;
    }

    const notConfirmableStatuses = [
        CheckoutStatus.Charged,
        CheckoutStatus.Canceled,
        CheckoutStatus.MarkedAsPaid,
        CheckoutStatus.PartiallyPaid,
    ];

    if (notConfirmableStatuses.includes(checkout.status)) {
        return false;
    }

    if (checkout.result?.paymentMethodOptions?.funds?.length) {
        return false;
    }

    if (checkout.result?.paymentOption?.paymentTerm?.type !== PaymentTermType.Immediate) {
        return false;
    }

    const isWire = checkout.result?.paymentOption?.paymentMethod === PaymentMethodType.Wire;

    return isWire || isScheduledPayment(checkout.result?.paymentDate);
};

export const canUseCurrencyExchange = (buyer: Buyer, currency: Currency, country?: Country, availableCurrencies?: Currency[]): boolean => {
    if (!country) {
        return true;
    }
    const buyerCurrency = buyer?.currency || BankConnectivityProvidersService.getCurrencyByCountry(country);
    if (!availableCurrencies?.includes(buyerCurrency)) {
        return false;
    }

    if (buyer?.tenantId === 'SHIP4WD-CA' || buyer?.tenantId === 'SHIP4WD') {
        return false;
    }

    return buyerCurrency !== currency;
};

export const isScheduledPayment = (paymentDate?: Date): boolean => {
    if (!paymentDate) {
        return false;
    }

    return moment(paymentDate).startOf('day').isAfter(moment().startOf('day'));
};

export const isTodayOrBefore = (date: Date): boolean => {
    return moment(date).startOf('day').isSameOrBefore(moment().startOf('day'));
};

export const calculateDueDate = (paymentTerm: PaymentTerm, checkoutDueDate: Date, invoice?: Invoice): Date | undefined => {
    if (paymentTerm.type === PaymentTermType.Immediate) {
        return checkoutDueDate;
    }

    const countFrom = paymentTerm?.payload?.countFrom;

    const fromDateAttribute = paymentTermTimingToDate.get(countFrom ? countFrom : PaymentTermTiming.FromInvoice);

    if (invoice) {
        if (fromDateAttribute && invoice?.[fromDateAttribute]) {
            const dueDate = new Date(invoice[fromDateAttribute]);

            const paymentDate = calculatePaymentDate(paymentTerm, dueDate);
            return paymentDate;
        } else {
            return undefined;
        }
    }

    if (countFrom !== PaymentTermTiming.FromDueDate) {
        return undefined;
    }

    const dueDate = new Date();

    const paymentDate = calculatePaymentDate(paymentTerm, dueDate);

    return paymentDate;
};

export const isOverdue = (dueDate: string | Date | Moment): boolean => {
    return moment(dueDate).startOf('day').isBefore(moment().startOf('day'));
};

export const calculatePaymentDate = (paymentTerm: PaymentTerm, dueDate: Date): Date | undefined => {
    if (paymentTerm.payload?.days) {
        dueDate.setDate(dueDate.getDate() + paymentTerm.payload.days);
    }

    const isDateInThePast = !!dueDate && moment.utc(dueDate).startOf('day').isBefore(moment.utc().startOf('day'));
    const paymentDate = isDateInThePast ? moment.utc() : moment.utc(dueDate);

    return paymentDate.toDate();
};

export const getPaymentMethodAndCurrency = (
    buyer: Buyer | undefined,
    currency: Currency | undefined,
): { currency: Currency | undefined; paymentMethodTypes: PaymentMethodType[] } => {
    if (!buyer) {
        return { currency: currency, paymentMethodTypes: [] };
    }

    // Buyer doesn't have a country, currency dropdown should be enabled
    if (!buyer?.address?.country) {
        return {
            currency: currency || Currency.USD,
            paymentMethodTypes: getPaymentMethodsByCurrency(currency, buyer?.address?.country),
        };
    }

    const paymentMethodType = BankConnectivityProvidersService.getPaymentMethodTypeByCountry(buyer?.address?.country);

    const currenciesByPaymentMethod = BankConnectivityProvidersService.getCurrencyByPaymentMethod(paymentMethodType);

    if (currenciesByPaymentMethod.length) {
        if (!buyer?.currency) {
            return { currency: currenciesByPaymentMethod[0], paymentMethodTypes: [paymentMethodType] };
        } else if (paymentMethodType && currenciesByPaymentMethod.includes(buyer?.currency)) {
            return { currency: buyer?.currency, paymentMethodTypes: [paymentMethodType] };
        }
    }

    if (currency === Currency.USD && buyer?.address?.country === Country.CANADA) {
        return {
            currency: currency,
            paymentMethodTypes: [PaymentMethodType.ACH, PaymentMethodType.ACSS],
        };
    }

    if (currency === Currency.CAD && buyer?.address?.country === Country.CANADA) {
        return {
            currency: currency,
            paymentMethodTypes: [PaymentMethodType.ACSS],
        };
    }

    return {
        currency: buyer?.currency,
        paymentMethodTypes: getPaymentMethodsByCurrency(buyer?.currency, buyer?.address?.country),
    };
};

const getPaymentMethodsByCurrency = (currency: Currency | undefined, country: Country | undefined): PaymentMethodType[] => {
    if (currency === undefined) {
        return [];
    }

    if (currency === Currency.USD && country === Country.CANADA) {
        return [PaymentMethodType.ACH, PaymentMethodType.ACSS];
    }

    if (currency === Currency.CAD && country === Country.CANADA) {
        return [PaymentMethodType.ACSS];
    }

    return [BankConnectivityProvidersService.getPaymentMethodByCurrency(currency) as PaymentMethodType];
};

export const mergePaymentOptionsByFinancing = (paymentOptions: PaymentOptionWithFee[]): PaymentOptionWithFee[] => {
    return paymentOptions.reduce((acc, cur) => {
        const existing = acc.find((p) => {
            const existingFinancingFee = p.paymentFees?.find((paymentFee) => paymentFee.feeDefinitionType === FeeDefinitionType.Financing);
            const currentFinancingFee = cur.paymentFees?.find((paymentFee) => paymentFee.feeDefinitionType === FeeDefinitionType.Financing);
            return existingFinancingFee?.feeDefinition === currentFinancingFee?.feeDefinition;
        });

        if (existing) {
            return acc;
        }

        return [...acc, cur];
    }, [] as PaymentOptionWithFee[]);
};

export const getInvoicePaymentTerm = (invoice?: Partial<Invoice>): PaymentTermType | undefined => {
    if (invoice?.paymentTerms?.length && invoice?.paymentTerms?.every((pt) => pt === PaymentTermType.Immediate)) {
        return PaymentTermType.Immediate;
    }

    if (invoice?.paymentTerms?.length && invoice?.paymentTerms?.every((pt) => pt === PaymentTermType.Net)) {
        return PaymentTermType.Net;
    }

    return invoice?.result?.paymentOptionWithFee?.paymentOption.paymentTerm.type;
};

export const getBuyerPaymentFee = (record?: Invoice): Fee[] => {
    if (!record) {
        return [];
    }

    if (record.result?.paymentOptionWithFee) {
        if (record.result?.paymentOptionWithFee?.paymentFees?.length) {
            return record.result?.paymentOptionWithFee?.paymentFees.filter((fee) => fee.feeDefinitionType === FeeDefinitionType.Financing);
        }

        // TODO: FS-5663 remove this condition after migration
        if (record.result?.paymentOptionWithFee?.paymentFee) {
            return [
                {
                    ...record.result?.paymentOptionWithFee?.paymentFee,
                    feeDefinitionType: FeeDefinitionType.Financing,
                },
            ];
        }
    }

    const paymentOptionsFinancingFee = record.paymentOptions?.[0]?.paymentFees?.find(
        (fee) => fee.feeDefinitionType === FeeDefinitionType.Financing,
    );

    if (
        paymentOptionsFinancingFee &&
        record.paymentOptions?.every(
            (p: PaymentOptionWithFee) =>
                p.paymentOption.paymentTerm.type === PaymentTermType.Net &&
                p.paymentFees?.find((fee: Fee) => fee.feeDefinitionType === FeeDefinitionType.Financing)?.feeDefinition ===
                    paymentOptionsFinancingFee?.feeDefinition,
        )
    ) {
        return [paymentOptionsFinancingFee];
    }

    return [];
};

export const getInvoiceCreditLineProvider = (invoice?: Partial<Invoice>): CreditLineProvider => {
    const resultProvider = invoice?.result?.paymentOptionWithFee?.paymentOption?.paymentTerm.provider;

    if (resultProvider) {
        return resultProvider;
    }

    if (
        invoice?.paymentOptions?.length &&
        invoice?.paymentOptions?.every((po) => po?.paymentOption?.paymentTerm?.provider === CreditLineProvider.Supplier)
    ) {
        return CreditLineProvider.Supplier;
    }

    return CreditLineProvider.Default;
};

export const convertFeeDefinitionToPaymentTermsOptions = (feeDefinitions: FeeDefinition[]): PaymentTermsOptions => {
    const netPaymentTermsOptions = feeDefinitions
        .filter(
            (feeDefinition) =>
                feeDefinition.type === FeeDefinitionType.Financing && feeDefinition.paymentTerm?.type === PaymentTermType.Net,
        )
        .map((feeDefinition) => {
            return {
                days: feeDefinition.paymentTerm?.payload?.days,
                countFrom: feeDefinition.paymentTerm?.payload?.countFrom || PaymentTermTiming.FromInvoice,
                provider: feeDefinition.paymentTerm?.provider || undefined,
                paymentMethods: feeDefinition.paymentMethods || [],
                feeDefinition: feeDefinition.id,
                buyerFee: feeDefinition.buyerFee,
                supplierFee: feeDefinition.supplierFee,
            };
        });

    const immediatePaymentTermsOptions = feeDefinitions.find(
        (feeDefinition) => feeDefinition.paymentTerm?.type !== PaymentTermType.Net && feeDefinition.type === FeeDefinitionType.Transaction,
    );

    const paymentTermsOptions: PaymentTermsOptions = {};

    if (immediatePaymentTermsOptions) {
        paymentTermsOptions[PaymentTermType.Immediate] = [];
    }

    if (netPaymentTermsOptions.length) {
        paymentTermsOptions[PaymentTermType.Net] = netPaymentTermsOptions;
    }

    return paymentTermsOptions;
};

const isValidPaymentMethod = (paymentMethodType: PaymentMethodType, bankPaymentMethodTypes: PaymentMethodType[]): boolean => {
    return (
        [PaymentMethodType.Card, PaymentMethodType.Wire].includes(paymentMethodType) || bankPaymentMethodTypes.includes(paymentMethodType)
    );
};

export const filterPaymentOptionsByPaymentMethod = (
    paymentOptionsWithFees: PaymentOptionWithFee[],
    bankPaymentMethodTypes: PaymentMethodType[],
): PaymentOptionWithFee[] => {
    return paymentOptionsWithFees.filter((po) => isValidPaymentMethod(po.paymentOption.paymentMethod, bankPaymentMethodTypes));
};

export const filterPaymentTermsOptionsByPaymentMethod = (
    paymentTermsOptions: PaymentTermsOptions,
    bankPaymentMethodTypes: PaymentMethodType[],
): PaymentTermsOptions => {
    return Object.fromEntries(
        Object.keys(paymentTermsOptions).map((key) => {
            const paymentOptions = paymentTermsOptions[key] || [];

            const filteredPaymentOptions = paymentOptions
                .map((paymentOption) => {
                    const filteredPaymentMethods = paymentOption.paymentMethods.filter((paymentMethodType) =>
                        isValidPaymentMethod(paymentMethodType, bankPaymentMethodTypes),
                    );

                    if (!filteredPaymentMethods.length) {
                        return undefined;
                    }

                    return { ...paymentOption, paymentMethods: filteredPaymentMethods };
                })
                .filter(Boolean);

            return [key, filteredPaymentOptions];
        }),
    );
};

export const getIsCheckoutPending = (status: CheckoutStatus) => {
    return CHECKOUT_PENDING_STATUSES.includes(status);
};

export const convertQuote = (quote: Quote): CurrencyExchange => {
    return {
        marketRate: quote.marketRate,
        rate: quote.rate,
        from: quote.from,
        to: quote.to,
        date: quote.createdAt,
        provider: quote.providerType as unknown as CurrencyExchangeProviderType,
        feeDefinition: quote.feeDefinition,
        providerQuoteId: quote.providerId,
        quote: quote.id,
    };
};

export const determinePaymentTerm = (checkout: Checkout, invoice?: Invoice): PaymentTermType => {
    const paymentTerm = invoice ? getInvoicePaymentTerm(invoice) : checkout.result?.paymentOptionWithFee?.paymentOption?.paymentTerm.type;
    return paymentTerm ?? PaymentTermType.Net;
};

export const hasApprovedCreditLineBySupplier = (creditLine?: CreditLine): boolean => {
    return creditLine?.result?.status === CreditLineResultStatus.Approved && creditLine?.provider === CreditLineProvider.Supplier;
};
